import { useEffect, useState } from "react";
import type { SpaceTransformationDashboard } from "~/utils/transformation-dashboard";

// The viewer's Transformation dashboard link for this mindspace, or null
// (apps/transformation/docs/plans/260924_mindspace-alias/PLAN.md). Fetched once
// per mindspace per tab and remembered in sessionStorage so the nav item does
// not pop in on every navigation. Every failure — network, storage, payload —
// resolves to null: no link is always the safe answer.

type CacheEntry = { dashboard: SpaceTransformationDashboard | null };

// eslint-disable-next-line lingui/no-unlocalized-strings -- storage key, not copy
const CACHE_PREFIX = "transformation-dashboard:v1:";
const memoryCache = new Map<string, Promise<SpaceTransformationDashboard | null>>();

function readCache(key: string): CacheEntry | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as CacheEntry) : null;
  } catch {
    return null;
  }
}

function writeCache(key: string, entry: CacheEntry): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // Storage full / blocked: the in-memory promise still dedupes this tab.
  }
}

async function load(mindspaceId: string): Promise<SpaceTransformationDashboard | null> {
  try {
    const response = await fetch(
      `/api/transformation-dashboard?mindspaceId=${encodeURIComponent(mindspaceId)}`,
      { headers: { accept: "application/json" }, credentials: "same-origin" },
    );
    if (!response.ok) return null;
    const payload = (await response.json()) as { dashboard?: SpaceTransformationDashboard | null };
    return payload.dashboard ?? null;
  } catch {
    return null;
  }
}

export function useTransformationDashboard(
  mindspaceId: string | null | undefined,
  enabled: boolean,
): SpaceTransformationDashboard | null {
  const key = mindspaceId ? `${CACHE_PREFIX}${mindspaceId}` : null;
  const [dashboard, setDashboard] = useState<SpaceTransformationDashboard | null>(null);

  useEffect(() => {
    if (!enabled || !key || !mindspaceId) {
      setDashboard(null);
      return;
    }
    const cached = readCache(key);
    if (cached) {
      setDashboard(cached.dashboard);
      return;
    }
    let cancelled = false;
    let pending = memoryCache.get(key);
    if (!pending) {
      pending = load(mindspaceId);
      memoryCache.set(key, pending);
    }
    pending.then((value) => {
      writeCache(key, { dashboard: value });
      if (!cancelled) setDashboard(value);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, key, mindspaceId]);

  return dashboard;
}

/** Leaders, admins and champions get the top-level nav item; learners use the home card. */
export function showsTransformationNav(dashboard: SpaceTransformationDashboard | null): boolean {
  return !!dashboard && (dashboard.audience !== "learner" || dashboard.isAdmin);
}
